// ============================================================================
// The boot/import flow, as a pure function: persisted blob + wall time ->
// ready session. Extracted from App.tsx so the exact code the component runs
// on reopen is headlessly testable (closed-tab catch-up, wall-clock rewind,
// death during catch-up, the maxAge clamp) — see apps/web/test/boot.test.ts.
// ============================================================================

import type { Params } from '@lifespan/engine';
import { ageAt, clampAge, pause, type Clock } from './clock.ts';
import { rebuild, settledAge, type Session } from './driver.ts';
import type { Persisted } from './persist.ts';

export interface BootState {
  session: Session;
  clock: Clock;
  awayDelta: { deltaS: number; fromAge: number; toAge: number } | null;
}

/**
 * Rebuild a persisted life and settle it to the current wall time.
 *
 * The rebuild target is floored at the save's lastSeen marker: a wall clock
 * that reads EARLIER at reopen than at the last close (NTP/OS correction, or
 * an export from a machine whose clock ran ahead) must never silently rewind
 * settled state — the log proves that life already happened. When the floor
 * binds, the clock is re-anchored forward so wall time resumes from the
 * proven extent instead of un-accruing it.
 */
export function bootFrom(stored: Persisted, nowMs: number, P: Params): BootState {
  let clock = stored.clock;
  let target = clampAge(ageAt(clock, nowMs), P.maxAge);
  const floor = stored.lastSeen ? clampAge(stored.lastSeen.age, P.maxAge) : P.startAge;
  // The proven extent, snapped UP to the substep grid. A live session's
  // lastSeen age is always substep-aligned (the ceil is exact); a DEAD life's
  // is its mid-substep deathAge, and the driver's floor rule would stop one
  // substep short of the fatal one — resurrecting the corpse with pre-death
  // state. The guard must therefore bind whenever the target lands anywhere
  // INSIDE the floor's substep, not only strictly below the floor: a dead-life
  // export re-imported at the same wall time reads target == floor == the
  // mid-substep deathAge (M6 regression: the corpse booted alive, permanently
  // parked one substep before death under its paused clock). Ceiling makes
  // the fatal substep re-execute; determinism reproduces the identical death.
  const floorGrid = clampAge(
    P.startAge + Math.ceil((floor - P.startAge) / P.substep - 1e-9) * P.substep,
    P.maxAge,
  );
  if (target < floorGrid) {
    target = floorGrid;
    clock = { ...clock, anchorAge: floor, anchorWallMs: nowMs };
  }
  const session = rebuild(stored.save, target, P);
  const settledNow = settledAge(session, P);
  let awayDelta: BootState['awayDelta'] = null;
  const seen = stored.lastSeen;
  // show the banner only when at least one statement landed while away
  if (seen && settledNow - seen.age >= P.substep - 1e-9 && session.state.S !== seen.S) {
    awayDelta = { deltaS: session.state.S - seen.S, fromAge: seen.age, toAge: settledNow };
  }
  if (!session.state.alive) clock = pause(clock, nowMs);
  return { session, clock, awayDelta };
}
